<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { api, messageFrom } from '../api'
import StatusBadge from '../components/StatusBadge.vue'
import { formatDate, formatDuration, formatTimecode } from '../format'
import type { RecordingDetails, TranscriptSegment } from '../types'

interface Paragraph { start: number; end: number; text: string }

const props = defineProps<{ id: string }>()
const emit = defineEmits<{ navigate: [path: string] }>()
const recording = ref<RecordingDetails | null>(null)
const player = ref<HTMLAudioElement | null>(null)
const currentTime = ref(0)
const editedText = ref('')
const editorDirty = ref(false)
const saving = ref(false)
const saved = ref(false)
const boundaryStartPhrase = ref('')
const boundaryEndPhrase = ref('')
const selectedBoundaryText = ref('')
const boundaryDirty = ref(false)
const savingBoundaries = ref(false)
const loading = ref(true)
const error = ref('')
let pollTimer: number | null = null

function paragraphsFrom(segments: TranscriptSegment[]): Paragraph[] {
  const result: Paragraph[] = []
  let current: Paragraph | null = null
  for (const segment of segments) {
    if (!current || segment.start - current.start >= 35 || current.text.length + segment.text.length > 420) {
      current = { start: segment.start, end: segment.end, text: segment.text.trim() }
      result.push(current)
    } else {
      current.end = segment.end
      current.text = `${current.text} ${segment.text.trim()}`.trim()
    }
  }
  return result
}

const paragraphs = computed(() => paragraphsFrom(recording.value?.transcript?.segments ?? []))
const activeParagraph = computed(() => {
  const items = paragraphs.value
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (currentTime.value >= (items[index]?.start ?? Infinity)) return index
  }
  return -1
})

function edgePhrase(text: string, fromStart: boolean): string {
  const words = text.trim().split(/\s+/).filter(Boolean)
  return (fromStart ? words.slice(0, 10) : words.slice(-10)).join(' ')
}

function savedBoundaryPhrases(details: RecordingDetails): { start: string; end: string } {
  const segments = details.transcript?.segments ?? []
  const startIndex = details.boundaries?.startSegment ?? 0
  const endIndex = details.boundaries?.endSegment ?? Math.max(0, segments.length - 1)
  const startSegment = segments[startIndex]
  const endSegment = segments[endIndex]
  if (!startSegment || !endSegment) return { start: '', end: '' }
  const startChar = details.boundaries?.startChar ?? 0
  const endChar = details.boundaries?.endChar ?? endSegment.text.length
  return {
    start: edgePhrase(startSegment.text.slice(startChar), true),
    end: edgePhrase(endSegment.text.slice(0, endChar), false),
  }
}

async function load(silent = false): Promise<void> {
  if (!silent) loading.value = true
  try {
    const next = (await api<{ recording: RecordingDetails }>(`/api/admin/recordings/${props.id}`)).recording
    recording.value = next
    if (!editorDirty.value && next.transcript) editedText.value = next.transcript.editedText ?? next.transcript.text
    if (!boundaryDirty.value && next.transcript) {
      const phrases = savedBoundaryPhrases(next)
      boundaryStartPhrase.value = phrases.start
      boundaryEndPhrase.value = phrases.end
    }
    error.value = ''
  } catch (reason) { error.value = messageFrom(reason) }
  finally { loading.value = false }
}

function seek(seconds: number): void {
  if (!player.value) return
  player.value.currentTime = seconds
  void player.value.play()
}

async function save(): Promise<void> {
  if (!recording.value?.transcript || saving.value) return
  saving.value = true
  saved.value = false
  try {
    await api(`/api/admin/recordings/${props.id}/transcript`, {
      method: 'PUT', body: JSON.stringify({ editedText: editedText.value }),
    })
    editorDirty.value = false
    saved.value = true
    await load(true)
    window.setTimeout(() => { saved.value = false }, 2_000)
  } catch (reason) { window.alert(messageFrom(reason)) }
  finally { saving.value = false }
}

async function retranscribe(): Promise<void> {
  if (!recording.value || !window.confirm('Снова запустить автоматическую расшифровку? Сохранённая ручная правка останется без изменений.')) return
  try {
    await api(`/api/admin/recordings/${props.id}/retranscribe`, { method: 'POST', body: '{}' })
    await load(true)
  } catch (reason) { window.alert(messageFrom(reason)) }
}

async function saveBoundaries(): Promise<void> {
  if (!recording.value?.transcript || savingBoundaries.value) return
  savingBoundaries.value = true
  try {
    const response = await api<{ recording: RecordingDetails }>(`/api/admin/recordings/${props.id}/boundaries`, {
      method: 'PUT',
      body: JSON.stringify({ startQuote: boundaryStartPhrase.value, endQuote: boundaryEndPhrase.value }),
    })
    recording.value = response.recording
    boundaryDirty.value = false
  } catch (reason) { window.alert(messageFrom(reason)) }
  finally { savingBoundaries.value = false }
}

function captureSelectedBoundaryText(): void {
  selectedBoundaryText.value = window.getSelection()?.toString().replace(/\s+/g, ' ').trim().slice(0, 1_000) ?? ''
}

function useSelectedBoundaryText(edge: 'start' | 'end'): void {
  if (!selectedBoundaryText.value) return
  if (edge === 'start') boundaryStartPhrase.value = selectedBoundaryText.value
  else boundaryEndPhrase.value = selectedBoundaryText.value
  boundaryDirty.value = true
}

onMounted(async () => {
  await load()
  await nextTick()
  pollTimer = window.setInterval(() => {
    if (recording.value?.status === 'queued' || recording.value?.status === 'transcribing'
      || (recording.value && ['pending', 'detecting', 'embedding'].includes(recording.value.knowledgeStatus))) void load(true)
  }, 3_000)
})
onBeforeUnmount(() => { if (pollTimer !== null) window.clearInterval(pollTimer) })
</script>

<template>
  <main class="page page--recording">
    <button v-if="recording" class="back-link" type="button" @click="emit('navigate', `/admin/speakers/${recording.speaker.id}`)">
      ← {{ recording.speaker.name }}
    </button>
    <p v-if="loading" class="quiet-message">Загружаем запись…</p>
    <div v-else-if="recording">
      <header class="recording-heading">
        <p class="eyebrow">Запись</p>
        <h1>{{ recording.title }}</h1>
        <p class="recording-meta">{{ formatDate(recording.createdAt) }} · {{ formatDuration(recording.durationSec) }}</p>
        <StatusBadge :recording="recording" />
      </header>

      <section class="player-card">
        <audio
          ref="player"
          controls
          preload="metadata"
          :src="`/api/admin/recordings/${recording.id}/audio`"
          @timeupdate="currentTime = ($event.target as HTMLAudioElement).currentTime"
        />
      </section>

      <div class="recording-toolbar">
        <a v-if="recording.transcript" class="button button--quiet" :href="`/api/admin/recordings/${recording.id}/transcript.txt`">Скачать TXT</a>
        <a v-if="recording.transcript" class="button button--quiet" :href="`/api/admin/recordings/${recording.id}/transcript.json`">Скачать JSON</a>
        <button class="button button--quiet" type="button" :disabled="recording.status === 'queued' || recording.status === 'transcribing'" @click="retranscribe">
          Перетранскрибировать
        </button>
      </div>

      <p v-if="recording.status === 'error'" class="error-panel">
        <strong>Расшифровку не удалось завершить.</strong><br />{{ recording.error }}
      </p>
      <p v-else-if="!recording.transcript" class="quiet-message quiet-message--panel">
        Расшифровка появится здесь автоматически. Страница обновляется каждые 3 секунды.
      </p>

      <template v-else>
        <section class="boundaries-section">
          <div class="section-heading">
            <div>
              <h2>Границы речи спикера</h2>
              <p>
                {{
                  recording.boundaryStatus === 'manual'
                    ? 'Исправлены вручную.'
                    : recording.boundaryStatus === 'pending'
                      ? 'Ещё не определены.'
                      : recording.boundaryStatus === 'fallback'
                        ? 'Автоматически определить не удалось.'
                        : 'Определены автоматически.'
                }}
                Исходная расшифровка не меняется.
              </p>
            </div>
          </div>

          <details v-if="recording.boundaries?.removedStartSegments.length" class="trimmed-block">
            <summary>В начале говорит не спикер · {{ recording.boundaries.removedStartSegments.length }} фрагм.</summary>
            <button
              v-for="(segment, index) in recording.boundaries.removedStartSegments"
              :key="`start-${index}`"
              type="button"
              @click="seek(segment.start)"
            >
              <time>{{ formatTimecode(segment.start) }}</time>{{ segment.text }}
            </button>
          </details>
          <details v-if="recording.boundaries?.removedEndSegments.length" class="trimmed-block">
            <summary>В конце говорит не спикер · {{ recording.boundaries.removedEndSegments.length }} фрагм.</summary>
            <button
              v-for="(segment, index) in recording.boundaries.removedEndSegments"
              :key="`end-${index}`"
              type="button"
              @click="seek(segment.start)"
            >
              <time>{{ formatTimecode(segment.start) }}</time>{{ segment.text }}
            </button>
          </details>
          <p v-if="recording.boundaries?.note" class="boundary-note">{{ recording.boundaries.note }}</p>
          <p
            v-else-if="recording.boundaries && !recording.boundaries.removedStartSegments.length && !recording.boundaries.removedEndSegments.length"
            class="boundary-note"
          >
            Ничего не отрезано.
          </p>

          <div v-if="recording.transcript.segments.length" class="boundary-editor">
            <label>
              Первые слова спикера
              <input
                v-model="boundaryStartPhrase"
                type="text"
                maxlength="1000"
                placeholder="Например: Привет, ребята. Спасибо, что пришли."
                @input="boundaryDirty = true"
              />
            </label>
            <label>
              Последние слова спикера
              <input
                v-model="boundaryEndPhrase"
                type="text"
                maxlength="1000"
                placeholder="Вставь последние 5–10 слов речи"
                @input="boundaryDirty = true"
              />
            </label>
            <p class="boundary-selection-hint">
              Выдели нужную фразу ниже в расшифровке, затем подставь её:
              <button class="button button--quiet" type="button" :disabled="!selectedBoundaryText" @click="useSelectedBoundaryText('start')">
                В начало
              </button>
              <button class="button button--quiet" type="button" :disabled="!selectedBoundaryText" @click="useSelectedBoundaryText('end')">
                В конец
              </button>
              <span v-if="selectedBoundaryText">Выделено: «{{ selectedBoundaryText }}»</span>
            </p>
            <button class="button button--primary" type="button" :disabled="savingBoundaries || !boundaryDirty" @click="saveBoundaries">
              {{ savingBoundaries ? 'Сохраняем…' : 'Сохранить границы' }}
            </button>
          </div>
          <p v-if="recording.knowledgeStatus === 'pending' || recording.knowledgeStatus === 'detecting'" class="knowledge-progress">
            Определяем границы речи…
          </p>
          <p v-else-if="recording.knowledgeStatus === 'embedding'" class="knowledge-progress">
            Строим эмбеддинги: {{ recording.knowledgeChunksDone }} из {{ recording.knowledgeChunksTotal }}
          </p>
          <p v-else-if="recording.knowledgeStatus === 'done'" class="text-success">
            База знаний готова: {{ recording.knowledgeChunksDone }} смысловых кусков.
          </p>
          <p v-else-if="recording.knowledgeStatus === 'error'" class="recording-error">
            {{ recording.knowledgeError }}
          </p>
        </section>

        <section class="transcript-section" @mouseup="captureSelectedBoundaryText">
          <div class="section-heading"><h2>Расшифровка с таймкодами</h2></div>
          <p v-if="!paragraphs.length" class="empty-state">В записи не распознана речь.</p>
          <button
            v-for="(paragraph, index) in paragraphs"
            :key="`${paragraph.start}-${index}`"
            class="transcript-paragraph"
            :class="{ 'transcript-paragraph--active': activeParagraph === index }"
            type="button"
            @click="seek(paragraph.start)"
          >
            <time>{{ formatTimecode(paragraph.start) }}</time>
            <span>{{ paragraph.text }}</span>
          </button>
        </section>

        <section class="editor-section">
          <div class="section-heading">
            <div>
              <h2>Текст для правки</h2>
              <p>Эта версия сохраняется отдельно и не сотрётся при новой транскрипции.</p>
            </div>
          </div>
          <textarea v-model="editedText" rows="18" @input="editorDirty = true; saved = false" />
          <div class="editor-actions">
            <button class="button button--primary" type="button" :disabled="saving || !editorDirty" @click="save">
              {{ saving ? 'Сохраняем…' : 'Сохранить правки' }}
            </button>
            <span v-if="saved" class="text-success">Сохранено</span>
          </div>
        </section>
      </template>
    </div>
    <p v-if="error" class="field-error" role="alert">{{ error }}</p>
  </main>
</template>
